import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, GraduationCap, ArrowLeft, Copy, Download, FileDown } from "lucide-react";
import { assignmentAssistant } from "@/lib/api/ai-research.functions";
import { CitationSetPicker } from "@/components/research/CitationSetPicker";
import type { CitationSet } from "@/lib/api/citation-sets.functions";
import { downloadResearchPdf } from "@/lib/research-pdf";

export const Route = createFileRoute("/education/research/assignment")({
  head: () => ({
    meta: [
      { title: "Assignment Assistant — Legal Research Hub | Nkadimeng Danny" },
      {
        name: "description",
        content:
          "AI Assignment Assistant for LLB students: plan, draft or improve a law assignment with OSCOLA-ready footnotes, a bibliography and a submission checklist.",
      },
      { property: "og:title", content: "Assignment Assistant — Legal Research Hub" },
      {
        property: "og:description",
        content:
          "Plan, draft or improve a law assignment with authority markers and a submission checklist.",
      },
    ],
  }),
  component: AssignmentPage,
});

type Mode = "plan" | "full_draft" | "improve";

function AssignmentPage() {
  const run = useServerFn(assignmentAssistant);
  const [module, setModule] = useState("");
  const [question, setQuestion] = useState("");
  const [wordLimit, setWordLimit] = useState(2000);
  const [citationStyle, setCitationStyle] = useState("OSCOLA");
  const [jurisdiction, setJurisdiction] = useState("South Africa");
  const [facts, setFacts] = useState("");
  const [sources, setSources] = useState("");
  const [mode, setMode] = useState<Mode>("plan");
  const [existingDraft, setExistingDraft] = useState("");
  const [citationSetId, setCitationSetId] = useState<string | null>(null);
  const [citationSet, setCitationSet] = useState<CitationSet | null>(null);
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);

  async function onRun(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    setOut(null);
    try {
      const r = await run({
        data: {
          module,
          question,
          wordLimit,
          citationStyle,
          jurisdiction,
          facts,
          sources,
          mode,
          existingDraft,
          citationSetId: citationSetId ?? undefined,
        },
      });
      setBalance(r.balance);
      if (r.ok) setOut(r.output);
      else setErr(r.error);
    } catch (e: any) {
      const msg = String(e?.message ?? "");
      if (msg.includes("INSUFFICIENT_CREDITS"))
        setErr("You've run out of AI credits. Buy more from your portal credits page.");
      else if (msg.includes("RATE_LIMIT"))
        setErr("Too many requests right now — try again in a minute.");
      else setErr("AI service is unavailable. Try again shortly.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-narrow py-10">
      <Link
        to="/education/research"
        className="inline-flex items-center gap-1 text-xs text-navy/70 hover:text-navy"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Research Hub
      </Link>
      <div className="mt-3 flex items-center gap-3">
        <span className="rounded-md bg-navy/5 p-2 text-navy">
          <GraduationCap className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-3xl text-navy">Assignment Assistant</h1>
          <p className="text-sm text-muted-foreground">
            Plan, draft or improve a law assignment — 1 credit per run
          </p>
        </div>
      </div>

      <div className="mt-6">
        <CitationSetPicker
          value={citationSetId}
          onChange={(id, set) => {
            setCitationSetId(id);
            setCitationSet(set);
          }}
        />
      </div>

      <form onSubmit={onRun} className="mt-4 space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="sm:col-span-1">
            <span className="text-sm font-semibold text-navy">Mode</span>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="plan">Plan (outline + authorities)</option>
              <option value="full_draft">Full draft</option>
              <option value="improve">Improve my draft</option>
            </select>
          </label>
          <label>
            <span className="text-sm font-semibold text-navy">Word limit</span>
            <input
              type="number"
              min={300}
              max={20000}
              value={wordLimit}
              onChange={(e) => setWordLimit(Number(e.target.value) || 2000)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          <label>
            <span className="text-sm font-semibold text-navy">Citation style</span>
            <select
              value={citationStyle}
              onChange={(e) => setCitationStyle(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option>OSCOLA</option>
              <option>Harvard</option>
              <option>APA 7</option>
              <option>SA citation</option>
            </select>
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label>
            <span className="text-sm font-semibold text-navy">Module (optional)</span>
            <input
              value={module}
              onChange={(e) => setModule(e.target.value)}
              maxLength={200}
              placeholder="e.g. PVL3701 — Law of Contract"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          <label>
            <span className="text-sm font-semibold text-navy">Jurisdiction</span>
            <input
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              maxLength={120}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>

        <div>
          <label className="text-sm font-semibold text-navy">Assignment question / brief</label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            required
            minLength={15}
            maxLength={4000}
            rows={5}
            placeholder="Paste the full assignment question exactly as given."
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-navy">
            Facts / scenario (if any)
          </label>
          <textarea
            value={facts}
            onChange={(e) => setFacts(e.target.value)}
            maxLength={8000}
            rows={4}
            placeholder="Paste the given scenario or facts, if the question includes one."
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-navy">
            Prescribed sources (optional)
          </label>
          <textarea
            value={sources}
            onChange={(e) => setSources(e.target.value)}
            maxLength={6000}
            rows={3}
            placeholder="One per line — cases, statutes, articles, textbooks the marker expects you to use."
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        {mode === "improve" && (
          <div>
            <label className="text-sm font-semibold text-navy">Your existing draft</label>
            <textarea
              value={existingDraft}
              onChange={(e) => setExistingDraft(e.target.value)}
              maxLength={30000}
              rows={10}
              placeholder="Paste your current draft. The assistant will rewrite it to a distinction standard."
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {existingDraft.length}/30000
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground disabled:opacity-60"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <GraduationCap className="h-4 w-4" />
          )}
          {mode === "plan"
            ? "Generate assignment plan (1 credit)"
            : mode === "full_draft"
              ? "Generate full draft (1 credit)"
              : "Improve my draft (1 credit)"}
        </button>
      </form>

      {balance !== null && (
        <p className="mt-3 text-xs text-muted-foreground">
          Credits remaining: <strong>{balance}</strong>
        </p>
      )}
      {err && <p className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-800">{err}</p>}

      {out && (
        <article className="mt-6 rounded-lg border border-input bg-card p-5">
          <div className="mb-3 flex flex-wrap justify-end gap-2">
            <button
              onClick={() =>
                downloadResearchPdf(
                  {
                    title: `Assignment — ${question.slice(0, 80) || "Law assignment"}`,
                    subtitle: module ? `Module: ${module}` : `Jurisdiction: ${jurisdiction}`,
                    citationSetName: citationSet?.name,
                    citationStyle: citationSet?.style ?? citationStyle,
                    body: out,
                  },
                  `assignment-${mode}.pdf`,
                )
              }
              className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90"
            >
              <FileDown className="h-3.5 w-3.5" /> Download PDF
            </button>
            <button
              onClick={() => {
                const blob = new Blob([out], { type: "text/markdown" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `assignment-${mode}.md`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs hover:bg-muted"
            >
              <Download className="h-3.5 w-3.5" /> .md
            </button>
            <button
              onClick={() => navigator.clipboard.writeText(out)}
              className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs hover:bg-muted"
            >
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
          </div>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
            {out}
          </pre>
          <p className="mt-4 border-t border-border pt-3 text-xs italic text-muted-foreground">
            AI-generated. Verify every authority and citation against the primary source before
            submitting.
          </p>
        </article>
      )}
    </div>
  );
}
